import { Trans } from "@lingui/macro";
import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router";

import { isDefinitiveAuthFailure } from "@/client/libs/auth-failure";
import { useUser } from "@/client/services/user";

import { needsOnboardingRedirect } from "./needs-onboarding";

/**
 * Growing backoff (ms) for AuthGuard's OWN re-probe of /user/me while a
 * TRANSIENT failure is showing the "reconnecting" screen: 5s, then +10s,
 * then +20s, then every 30s thereafter, forever (or until the tab is
 * closed / navigates away).
 *
 * Codex review on PR #577 (DO NOT MERGE #1): axios.ts's `/auth/refresh`
 * retry (`AUTH_REFRESH_RETRY_DELAYS_MS`) is a short ~3s burst by design — it
 * blocks every OTHER request queued behind it, so it can't be the thing
 * that rides out a real outage. THIS schedule is that thing: it lives only
 * in the guard, doesn't block anything, and is the only long-lived retry in
 * the whole flow (`useUser` itself is `retry: false` again — see user.ts).
 * It resets to the start (5s) the next time a transient failure BEGINS
 * (i.e. any earlier refetch succeeded, clearing this effect, and a LATER
 * one fails again) — a brief blip never leaves a tab stuck polling at the
 * 30s ceiling forever.
 */
export const RECONNECT_POLL_SCHEDULE_MS = [5000, 10_000, 20_000, 30_000];

export const AuthGuard = () => {
  const location = useLocation();
  const redirectTo = location.pathname + location.search;

  // force: true → ignore the localStorage hint short-circuit. Cookies are
  // authoritative; only redirect after /me actually 401s (axios refresh
  // interceptor gets a shot first, so a fresh tab with a valid refresh
  // cookie still resolves cleanly).
  const { user, loading, error, isError, refetch } = useUser({ force: true });

  // Regression (2026-09-03): a transient /user/me failure (502 during a
  // deploy, a brief upstream stall) used to read as "logged out" and bounce
  // a still-valid session to /auth/login. Only redirect on a DEFINITIVE
  // failure (401/403) or a clean "no user" result — a transient error keeps
  // the visitor on a "reconnecting" screen that quietly retries instead.
  const isTransientFailure = isError && !isDefinitiveAuthFailure(error);

  useEffect(() => {
    if (!isTransientFailure) return;

    // A `setTimeout` CHAIN, not `setInterval` — the delay must GROW between
    // calls (5s, 10s, 20s, then a flat 30s), which a fixed-period interval
    // cannot express. `step` and `timeoutId` are effect-local: React tears
    // this whole closure down and re-runs it fresh whenever
    // `isTransientFailure` flips, which is exactly how the schedule resets
    // to 5s the next time a NEW transient failure begins.
    let cancelled = false;
    let step = 0;
    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNext = () => {
      const delayIndex = Math.min(step, RECONNECT_POLL_SCHEDULE_MS.length - 1);
      const delay = RECONNECT_POLL_SCHEDULE_MS[delayIndex];
      timeoutId = setTimeout(() => {
        if (cancelled) return;
        step += 1;
        void refetch();
        scheduleNext();
      }, delay);
    };

    scheduleNext();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isTransientFailure, refetch]);

  if (loading) return null;

  if (user) {
    // New users go through the wizard before anything else — nothing else in
    // the app routes them there (OAuth callbacks land on /dashboard).
    if (needsOnboardingRedirect(user, location.pathname)) {
      return <Navigate replace to="/onboarding" />;
    }
    return <Outlet />;
  }

  if (isTransientFailure) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p role="status">
          <Trans>Reconnecting to Apply Edge…</Trans>
        </p>
      </div>
    );
  }

  return <Navigate replace to={`/auth/login?redirect=${encodeURIComponent(redirectTo)}`} />;
};
